export function hexToRgba(hex: string, opacity: number): string {
    if (hex.startsWith("#")) {
        hex = hex.slice(1)
    }

    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export interface ThemeVariables {
    [key: string]: string
}

export const setTheme = (
    root: HTMLElement,
    theme: string,
    customVariables?: ThemeVariables,
    scoped?: boolean,
) => {
    const applyVariables = (element: HTMLElement, vars: ThemeVariables) => {
        Object.entries(vars).forEach(([key, value]) => {
            element.style.setProperty(key, value)
        })
    }
    if (!scoped) {
        document.body.removeAttribute("data-theme")
        document.body.setAttribute("data-theme", theme ?? "default")
    }

    // Porsche
    if (theme === "dark-blue") {
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#001c2f")
        root.style.setProperty("--theme-secondary", "#ffffff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#ffffff", 0.5))
        root.style.setProperty("--theme-additional", "#28d5f9")
        root.style.setProperty("--theme-shade", "#36475a")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#36475a", 0.5))

        root.style.setProperty("--theme-tertiary", "#28d5f9")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#28d5f9", 0.25))
        root.style.setProperty("--animation-export-color", "#28d5f9")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#28d5f9", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#28d5f9")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #001420, #001c2f) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_blue.svg'), linear-gradient(0deg, #001420 0%, #001c2f 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_dark_blue.svg'), linear-gradient(0deg, #001420 0%, #001c2f 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#001420", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#000e14", 0.9))
        root.style.setProperty("--toast-text", "#ffffff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "white") {
        // TAB
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#ffffff")
        root.style.setProperty("--theme-secondary", "#43474b")
        root.style.setProperty("--theme-auxiliary", "#666666")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#43474b", 0.5))
        root.style.setProperty("--theme-additional", "#43474b")
        root.style.setProperty("--theme-shade", "#009a4e")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#009a4e", 0.5))
        root.style.setProperty("--theme-tertiary", "#009a4e")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#009a4e", 0.25))
        root.style.setProperty("--animation-export-color", "#009a4e")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#009a4e", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#009a4e")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #ffffff, #ffffff) 0 0 no-repeat padding-box",
        )
        root.style.setProperty("--bg-image", "url('/img/backgrounds/house_white.svg')")
        root.style.setProperty("--bg-image-public", "url('/img/backgrounds/house_white.svg')")

        root.style.setProperty("--toast-bg", hexToRgba("#ffffff", 0.95))
        root.style.setProperty("--toast-header-bg", hexToRgba("#f5f5f5", 0.95))
        root.style.setProperty("--toast-text", "#43474b")
        root.style.setProperty("--toast-border", hexToRgba("#43474b", 0.2))
    } else if (theme === "yellow") {
        // GL Charge
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#141414")
        root.style.setProperty("--theme-secondary", "#fcfcfc")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fcfcfc", 0.5))
        root.style.setProperty("--theme-additional", "#8B91A8")
        root.style.setProperty("--theme-shade", "#e2e046")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#e2e046", 0.5))
        root.style.setProperty("--theme-tertiary", "#e2e046")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#e2e046", 0.25))
        root.style.setProperty("--animation-export-color", "#e2e046")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#e2e046", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#e2e046")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #000d15, #141414) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_yellow.svg'), linear-gradient(0deg, #000d15 0%, #141414 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_yellow.svg'), linear-gradient(0deg, #000d15 0%, #141414 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#141414", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#0a0a0a", 0.9))
        root.style.setProperty("--toast-text", "#fcfcfc")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "dark-green") {
        // FlexGrid
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#231f20")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#b3b3b3")
        root.style.setProperty("--theme-shade", "#55af3a")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#55af3a", 0.5))
        root.style.setProperty("--theme-tertiary", "#55af3a")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#55af3a", 0.25))
        root.style.setProperty("--animation-export-color", "#55af3a")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#55af3a", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#55af3a")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #231f20, #161414) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_green.svg'), linear-gradient(0deg, #231f20 0%, #161414 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_dark_green.svg'), linear-gradient(0deg, #231f20 0%, #161414 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#231f20", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#161414", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "red-gray") {
        // Termoshop
        root.style.setProperty("--font-family", "Archivo")

        root.style.setProperty("--theme-primary", "#333F48")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#333F48")
        root.style.setProperty("--theme-shade", "#00AF66")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#00AF66", 0.5))
        root.style.setProperty("--theme-tertiary", "#00AF66")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#00AF66", 0.25))
        root.style.setProperty("--animation-export-color", "#00AF66")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#00AF66", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#00AF66")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #333F48, #191f24) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_red_gray.svg'), linear-gradient(0deg, #191f24 0%, #333F48 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_red_gray.svg'), linear-gradient(0deg, #191f24 0%, #333F48 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#333F48", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#191f24", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "energy-blue") {
        // e-Voltra
        root.style.setProperty("--font-family", "Figtree")

        root.style.setProperty("--theme-primary", "#000000")
        root.style.setProperty("--theme-secondary", "#ffffff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#ffffff", 0.5))
        root.style.setProperty("--theme-additional", "#1c75bc")
        root.style.setProperty("--theme-shade", "#1c75bc")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#1c75bc", 0.5))
        root.style.setProperty("--theme-tertiary", "#2abdb9")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#2abdb9", 0.25))
        root.style.setProperty("--animation-export-color", "#2abdb9")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#2abdb9", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#1c75bc")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #0c0c0c, #131313) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_energy_blue.svg'), linear-gradient(0deg, #131313 0%, #0c0c0c 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_energy_blue.svg'), linear-gradient(0deg, #131313 0%, #0c0c0c 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#131313", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#0c0c0c", 0.9))
        root.style.setProperty("--toast-text", "#ffffff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "dark-orange") {
        // ENSET
        root.style.setProperty("--font-family", "Open Sans")
        root.style.setProperty("--theme-primary", "#042a33")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#193638")
        root.style.setProperty("--theme-shade", "#009D9D")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#009D9D", 0.5))
        root.style.setProperty("--theme-tertiary", "#009D9D")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#009D9D", 0.25))
        root.style.setProperty("--animation-export-color", "#009D9D")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#009D9D", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#009D9D")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #063642, #052e38) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_orange.svg'), linear-gradient(0deg, #052e38 0%, #063642 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/enset.png'), linear-gradient(0deg, #052e38 0%, #063642 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#042a33", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#052e38", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "green-gray") {
        // Merkur Energija
        root.style.setProperty("--font-family", "Intelo")

        root.style.setProperty("--theme-primary", "#535353")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#ffdd00")
        root.style.setProperty("--theme-shade", "#009640")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#009640", 0.5))
        root.style.setProperty("--theme-tertiary", "#009640")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#009640", 0.25))
        root.style.setProperty("--animation-export-color", "#ffdd00")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#ffdd00", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#ffdd00")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #535353, #3f3f3f) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_green_gray.svg'), linear-gradient(0deg, #535353 0%, #3f3f3f 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_green_gray.svg'), linear-gradient(0deg, #535353 0%, #3f3f3f 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#535353", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#3f3f3f", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "white-gray") {
        // TellCo Europe
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#EEEEED")
        root.style.setProperty("--theme-secondary", "#5C6065")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#5C6065", 0.5))
        root.style.setProperty("--theme-additional", "#5C6065")
        root.style.setProperty("--theme-shade", "#FC0000")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#FC0000", 0.5))
        root.style.setProperty("--theme-gradient-background", "#EEEEED")
        root.style.setProperty("--theme-tertiary", "#FC0000")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#FC0000", 0.25))
        root.style.setProperty("--animation-export-color", "#009a4e")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#009a4e", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#FC0000")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_white_gray.svg'), linear-gradient(0deg, #EEEEED 0%, #EEEEED 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/background_white_gray.jpg'), linear-gradient(0deg, #EEEEED 0%, #EEEEED 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#EEEEED", 0.95))
        root.style.setProperty("--toast-header-bg", hexToRgba("#dcdcdc", 0.95))
        root.style.setProperty("--toast-text", "#5C6065")
        root.style.setProperty("--toast-border", hexToRgba("#5C6065", 0.2))
    } else if (theme === "yellow-blue") {
        // ECE
        root.style.setProperty("--font-family", "Figtree")

        root.style.setProperty("--theme-primary", "#231f20")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#FBB116")
        root.style.setProperty("--theme-shade", "#00599F")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#00599F", 0.5))
        root.style.setProperty("--theme-tertiary", "#FBB116")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#FBB116", 0.25))
        root.style.setProperty("--animation-export-color", "#FBB116")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#FBB116", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#00599F")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #231f20, #1b1819) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_yellow_blue.svg'), linear-gradient(0deg, #231f20 0%, #1b1819 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_yellow_blue.svg'), linear-gradient(0deg, #231f20 0%, #1b1819 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#231f20", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#1b1819", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "dark-yellow-blue") {
        // ChargeHome
        root.style.setProperty("--font-family", "Figtree")
        root.style.setProperty("--theme-primary", "#333F48")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#E7B44E")
        root.style.setProperty("--theme-shade", "#79cad8")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#79cad8", 0.5))
        root.style.setProperty("--theme-tertiary", "#E7B44E")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#E7B44E", 0.25))
        root.style.setProperty("--animation-export-color", "#E7B44E")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#E7B44E", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#79cad8")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #333F48, #191f24) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_yellow_blue.svg'), linear-gradient(0deg, #333F48 0%, #191f24 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_dark_yellow_blue.svg'), linear-gradient(0deg, #333F48 0%, #191f24 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#333F48", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#191f24", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "blue-green") {
        //AmpSavers
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#1F1F1F")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#00E2F1")
        root.style.setProperty("--theme-shade", "#B3ED00")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#B3ED00", 0.5))
        root.style.setProperty("--theme-tertiary", "#00E2F1")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#00E2F1", 0.25))
        root.style.setProperty("--animation-export-color", "#B3ED00")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#B3ED00", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#B3ED00")
        root.style.setProperty("--theme-gradient-background", "#141414")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_blue_green.svg'), linear-gradient(0deg, #141414 0%, #141414 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_blue_green.svg'), linear-gradient(0deg, #141414 0%, #141414 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#1F1F1F", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#141414", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "energy-cyan") {
        //Gen-i
        root.style.setProperty("--font-family", "Open Sans")
        root.style.setProperty("--theme-primary", "#024053")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#00A796")
        root.style.setProperty("--theme-shade", "#00A796")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#00A796", 0.5))
        root.style.setProperty("--theme-tertiary", "#fcb637")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#fcb637", 0.25))
        root.style.setProperty("--animation-export-color", "#fcb637")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#fcb637", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#00A796")
        root.style.setProperty("--theme-gradient-background", "#005671")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_energy_cyan.svg'), linear-gradient(0deg, #024053 0%, #024053 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_energy_cyan.svg'), linear-gradient(0deg, #024053 0%, #024053 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#024053", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#005671", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "dark-cyan") {
        // EVIO theme
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#1E2025")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#C8CBC8")
        root.style.setProperty("--theme-shade", "#5B627B")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#5B627B", 0.5))
        root.style.setProperty("--theme-tertiary", "#6AF9CD")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#6AF9CD", 0.25))
        root.style.setProperty("--animation-export-color", "#6AF9CD")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#6AF9CD", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#5B627B")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #1E2025 0%, #1E2025 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_cyan.svg'), linear-gradient(149deg, #1E2025 0%, #1E2025  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_dark_cyan.svg'), linear-gradient(149deg, #1E2025 0%, #1E2025  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#1E2025", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#14161b", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "white-blue") {
        //veltol
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#fff")
        root.style.setProperty("--theme-secondary", "#2d458c")
        root.style.setProperty("--theme-auxiliary", "#d7e9f7")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#2d458c", 0.5))
        root.style.setProperty("--theme-additional", "#2d458c")
        root.style.setProperty("--theme-shade", "#60d8a1")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#60d8a1", 0.5))
        root.style.setProperty("--theme-gradient-background", "#fff")
        root.style.setProperty("--theme-tertiary", "#60d8a1")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#60d8a1", 0.25))
        root.style.setProperty("--animation-export-color", "#2d458c")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#2d458c", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#2d458c")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_white_blue.svg'), linear-gradient(0deg, #fff 0%, #fff 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_white_blue.svg'), linear-gradient(0deg, #fff 0%, #fff 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#ffffff", 0.95))
        root.style.setProperty("--toast-header-bg", hexToRgba("#d7e9f7", 0.95))
        root.style.setProperty("--toast-text", "#2d458c")
        root.style.setProperty("--toast-border", hexToRgba("#2d458c", 0.2))
    } else if (theme === "dark-light-blue") {
        //reteco/micoma
        root.style.setProperty("--font-family", "Montserrat")
        root.style.setProperty("--theme-primary", "#000000")
        root.style.setProperty("--theme-secondary", "#ffffff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#ffffff", 0.5))
        root.style.setProperty("--theme-additional", "#4DC0DF")
        root.style.setProperty("--theme-shade", "#4DC0DF")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#4DC0DF", 0.5))
        root.style.setProperty("--theme-tertiary", "#4DC0DF")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#4DC0DF", 0.25))
        root.style.setProperty("--animation-export-color", "#4DC0DF")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#4DC0DF", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#4DC0DF")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(180deg, #0c0c0c, #131313) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_dark_light_blue.svg'), linear-gradient(0deg, #131313 0%, #0c0c0c 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_dark_light_blue.svg'), linear-gradient(0deg, #131313 0%, #0c0c0c 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#131313", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#0c0c0c", 0.9))
        root.style.setProperty("--toast-text", "#ffffff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "blue-orange") {
        // EAB - alinea
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#113355")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#D5941A")
        root.style.setProperty("--theme-shade", "#267053")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#267053", 0.5))
        root.style.setProperty("--theme-tertiary", "#D5941A")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#D5941A", 0.25))
        root.style.setProperty("--animation-export-color", "#D5941A")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#D5941A", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#D5941A")
        root.style.setProperty("--theme-gradient-background", "#113355")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_blue_orange.svg'), linear-gradient(0deg, #0a1e32 0%, #0a1e32 100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_blue_orange.svg'), linear-gradient(0deg, #0a1e32 0%, #0a1e32 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#113355", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#0a1e32", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "emerald-white") {
        //enna next
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#D0E2E4")
        root.style.setProperty("--theme-secondary", "#005D4E")
        root.style.setProperty("--theme-auxiliary", "#0e9681")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#005d4e", 0.5))
        root.style.setProperty("--theme-additional", "#005d4e")
        root.style.setProperty("--theme-shade", "#004D40")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#004D40", 0.5))
        root.style.setProperty("--theme-gradient-background", "#d0e2e4")
        root.style.setProperty("--theme-tertiary", "#004c40")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#D0E2E4", 0.25))
        root.style.setProperty("--animation-export-color", "#004c40")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#004c40", 0.25))
        root.style.setProperty("--animation-import-color", "#811900")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#811900", 0.25))
        root.style.setProperty("--animation-home-import-color", "#004D40")
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_emerald_white.svg'), linear-gradient(0deg, #d0e2e4 0%, #d0e2e4 100%)",
        )

        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/background_emerald_white.jpg'), linear-gradient(0deg, #d0e2e4 0%, #d0e2e4 100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#D0E2E4", 0.95))
        root.style.setProperty("--toast-header-bg", hexToRgba("#bed2d7", 0.95))
        root.style.setProperty("--toast-text", "#005D4E")
        root.style.setProperty("--toast-border", hexToRgba("#005D4E", 0.2))
    } else if (theme === "blue-gold") {
        //sonnen
        root.style.setProperty("--font-family", "Archivo")

        root.style.setProperty("--theme-primary", "#141f3d")
        root.style.setProperty("--theme-secondary", "#ffffff")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#ffffff", 0.5))
        root.style.setProperty("--theme-auxiliary", "#c9cbc8")
        root.style.setProperty("--theme-additional", "#f5f5f5")
        root.style.setProperty("--theme-shade", "#668cfc")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#668cfc", 0.5))
        root.style.setProperty("--theme-tertiary", "#99b3fd")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#99b3fd", 0.25))
        root.style.setProperty("--animation-export-color", "#99b3fd")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#99b3fd", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#668cfc")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #091635 0%, #091635 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_blue_gold.svg'), linear-gradient(149deg, #091635 0%, #091635  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_blue_gold.svg'), linear-gradient(149deg, #091635 0%, #091635  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#141f3d", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#091635", 0.9))
        root.style.setProperty("--toast-text", "#ffffff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "mid-green") {
        //Evolut-ion
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#002a20")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-secondary-transparent-50", "rgba(255, 255, 255, 0.5)")
        root.style.setProperty("--theme-auxiliary", "#02d683")
        root.style.setProperty("--theme-additional", "#ffffff")
        root.style.setProperty("--theme-shade", "#02d683")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#02d683", 0.5))
        root.style.setProperty("--theme-tertiary", "#02d683")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#02d683", 0.25))
        root.style.setProperty("--animation-export-color", "#02d683")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#02d683", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#02d683")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #002a20 0%, #002a20 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_mid_green.svg'), linear-gradient(149deg, #002a20 0%, #002a20  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_mid_green.svg'), linear-gradient(149deg, #002a20 0%, #002a20  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#002a20", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#001a14", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "navy-gold") {
        //JB Energija
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#1d2832")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-additional", "#C8CBC8")
        root.style.setProperty("--theme-shade", "#f5b52a")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#f5b52a", 0.5))
        root.style.setProperty("--theme-tertiary", "#f5b52a")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#f5b52a", 0.25))
        root.style.setProperty("--animation-export-color", "#f5b52a")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#f5b52a", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#f5b52a")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #283542 0%, #1d2832 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_navy_gold.svg'), linear-gradient(149deg, #1d2832 0%, #1d2832  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_navy_gold.svg'), linear-gradient(149deg, #1d2832 0%, #1d2832  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#1d2832", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#141e28", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else if (theme === "charcoal-blue") {
        root.style.setProperty("--font-family", "Montserrat")

        root.style.setProperty("--theme-primary", "#161717")
        root.style.setProperty("--theme-secondary", "#ffffff")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#ffffff", 0.5))
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-additional", "#ffffff")
        root.style.setProperty("--theme-shade", "#00a1fd")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#00a1fd", 0.5))
        root.style.setProperty("--theme-tertiary", "#00a1fd")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#00a1fd", 0.25))
        root.style.setProperty("--animation-export-color", "#00a1fd")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#00a1fd", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#00a1fd")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #191a1a 0%, #1c2730 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/house_charcoal_blue.svg'), linear-gradient(149deg, #161717 0%, #161717  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/house_charcoal_blue.svg'), linear-gradient(149deg, #161717 0%, #161717  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#161717", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#0f1010", 0.9))
        root.style.setProperty("--toast-text", "#ffffff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    } else {
        // Default theme REDUXI
        root.style.setProperty("--font-family", "Open Sans")

        root.style.setProperty("--theme-primary", "#1d2832")
        root.style.setProperty("--theme-secondary", "#fff")
        root.style.setProperty("--theme-auxiliary", "#C8CBC8")
        root.style.setProperty("--theme-secondary-transparent-50", hexToRgba("#fff", 0.5))
        root.style.setProperty("--theme-additional", "#C8CBC8")
        root.style.setProperty("--theme-shade", "#0db4ac")
        root.style.setProperty("--theme-shade-transparent-50", hexToRgba("#0db4ac", 0.5))
        root.style.setProperty("--theme-tertiary", "#0db4ac")
        root.style.setProperty("--theme-tertiary-25", hexToRgba("#0db4ac", 0.25))
        root.style.setProperty("--animation-export-color", "#0db4ac")
        root.style.setProperty("--animation-export-color-25", hexToRgba("#0db4ac", 0.25))
        root.style.setProperty("--animation-import-color", "#ff1e47")
        root.style.setProperty("--animation-import-color-25", hexToRgba("#ff1e47", 0.25))
        root.style.setProperty("--animation-home-import-color", "#0db4ac")
        root.style.setProperty(
            "--theme-gradient-background",
            "transparent linear-gradient(148deg, #283542 0%, #1d2832 100%) 0 0 no-repeat padding-box",
        )
        root.style.setProperty(
            "--bg-image",
            "url('/img/backgrounds/reduxi.svg'), linear-gradient(149deg, #1d2832 0%, #1d2832  100%)",
        )
        root.style.setProperty(
            "--bg-image-public",
            "url('/img/backgrounds/reduxi.svg'), linear-gradient(149deg, #1d2832 0%, #1d2832  100%)",
        )

        root.style.setProperty("--toast-bg", hexToRgba("#1d2832", 0.9))
        root.style.setProperty("--toast-header-bg", hexToRgba("#141e28", 0.9))
        root.style.setProperty("--toast-text", "#fff")
        root.style.setProperty("--toast-border", hexToRgba("#ffffff", 0.15))
    }

    root.style.setProperty("--toast-danger-accent", "#d94040")
    root.style.setProperty("--toast-warning-accent", "#e8a020")
    root.style.setProperty("--toast-success-accent", "#34a35a")
    root.style.setProperty("--toast-info-accent", "#3c81ef")

    if (customVariables) {
        applyVariables(root, customVariables)
    }
}
